/**
 * BiometricEnrollPrompt — after the user logs in on the native app, offer to
 * enable fingerprint unlock for next time. Shown once per session (until the
 * user accepts or dismisses); never on the web.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Fingerprint } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { useAuthStore } from '../stores/auth-store';
import { getAuthToken } from '../lib/api';
import {
  isNative,
  isBiometricEnabled,
  biometricAvailable,
  enableBiometric,
} from '../lib/biometric';
import { notify } from '../lib/toast';

const DISMISS_KEY = 'reformulat_biometric_prompt_dismissed';

export function BiometricEnrollPrompt() {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function maybeOffer() {
      if (!isNative() || !isAuthenticated || !user) return;
      if (isBiometricEnabled()) return;
      try {
        if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
      } catch {
        /* ignore */
      }
      if (!getAuthToken()) return; // no token to protect
      const available = await biometricAvailable();
      if (!cancelled && available) setOpen(true);
    }
    void maybeOffer();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const enable = async () => {
    const token = getAuthToken();
    if (!user?.email || !token) {
      dismiss();
      return;
    }
    const ok = await enableBiometric(user.email, token);
    if (ok) {
      notify.success(
        t('biometricEnroll.enabledTitle', 'Desbloqueio por digital ativado'),
        t('biometricEnroll.enabledDescription', 'Da próxima vez é só usar sua digital.')
      );
    } else {
      notify.error(
        t('biometricEnroll.errorTitle', 'Não foi possível ativar'),
        t('biometricEnroll.errorDescription', 'Tente novamente nas configurações.')
      );
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <ResponsiveModal open={open} onOpenChange={(o) => (o ? setOpen(true) : dismiss())}>
      <ResponsiveModalContent className="sm:max-w-[420px]">
        <ResponsiveModalHeader>
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Fingerprint size={28} className="text-primary" />
          </div>
          <ResponsiveModalTitle className="text-center">{t('biometricEnroll.title', 'Entrar mais rápido')}</ResponsiveModalTitle>
          <ResponsiveModalDescription className="text-center">
            {t(
              'biometricEnroll.description',
              'Ative o desbloqueio por digital para abrir o reformu.lat sem digitar a senha da próxima vez.'
            )}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>
        <ResponsiveModalFooter className="flex gap-2 sm:justify-center">
          <Button variant="outline" onClick={dismiss}>
            {t('biometricEnroll.notNow', 'Agora não')}
          </Button>
          <Button onClick={enable} className="gap-2">
            <Fingerprint size={16} />
            {t('biometricEnroll.enableBiometric', 'Ativar digital')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
